import { z } from 'zod';
import { PaginationSchema } from './task.schema';

// ─── Status Schemas ───

/**
 * Semantic reporting category for a status (ADR 0014). The single authoritative guard for the
 * six fixed cross-list buckets — imported by the report schemas and mirrored by the hand-written
 * `StatusCategory` union in `../types/task.ts`. Values align 1:1 with the `tailwind.config.ts`
 * design tokens (`status.todo` / `.in-progress` / `.review` / `.blocked` / `.done` / `.cancelled`).
 */
export const StatusCategoryEnum = z.enum([
  'TODO',
  'IN_PROGRESS',
  'REVIEW',
  'BLOCKED',
  'DONE',
  'CANCELLED',
]);

/**
 * Validation schema for creating a new status (Kanban column).
 * Used on POST /api/v1/statuses.
 * @example
 * const input = CreateStatusSchema.parse({
 *   name: 'In Progress',
 *   color: '#3B82F6',
 *   category: 'IN_PROGRESS',
 *   listId: 'list-uuid',
 * });
 */
export const CreateStatusSchema = z.object({
  /** Status name (required, 1-100 characters) */
  name: z.string().min(1).max(100),
  /** Hex color code (optional, defaults to #6B7280 gray) */
  color: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/, 'color.invalidHex')
    .optional()
    .default('#6B7280'),
  /** Semantic reporting category (optional, defaults to TODO — ADR 0014). */
  category: StatusCategoryEnum.optional().default('TODO'),
  /** ID of the list this status belongs to (required) */
  listId: z.string().uuid(),
});

/** Type inferred from CreateStatusSchema */
export type CreateStatusInput = z.infer<typeof CreateStatusSchema>;

/**
 * Validation schema for updating an existing status.
 * Used on PATCH /api/v1/statuses/{id}.
 * At least one field must be provided.
 * @example
 * const input = UpdateStatusSchema.parse({
 *   name: 'Updated Name',
 * });
 */
export const UpdateStatusSchema = z
  .object({
    /** Updated status name */
    name: z.string().min(1).max(100).optional(),
    /** Updated hex color code */
    color: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/, 'color.invalidHex')
      .optional(),
    /** Updated position (for reordering columns) */
    position: z.number().int().nonnegative().optional(),
    /** Updated semantic reporting category (ADR 0014). */
    category: StatusCategoryEnum.optional(),
  })
  .refine((data) => Object.values(data).some((v) => v !== undefined), {
    // Stable message KEY, not prose (Batch B7, docs/phase9/batch-b-i18n.md §2 Pattern A).
    message: 'common.atLeastOneFieldRequired',
  });

/** Type inferred from UpdateStatusSchema */
export type UpdateStatusInput = z.infer<typeof UpdateStatusSchema>;

/**
 * Validation schema for querying statuses by list.
 * Used on GET /api/v1/statuses?listId=...
 * @example
 * const query = StatusListQuerySchema.parse({ listId: 'list-uuid' });
 */
export const StatusListQuerySchema = z.object({
  /** ID of the list to fetch statuses for (required) */
  listId: z.string().uuid(),
});

/** Type inferred from StatusListQuerySchema */
export type StatusListQuery = z.infer<typeof StatusListQuerySchema>;

// ─── User List Schemas ───

/**
 * Validation schema for querying users.
 * Used on GET /api/v1/users.
 * Supports pagination and search.
 * @example
 * const query = UserListQuerySchema.parse({
 *   limit: 50,
 *   search: 'john',
 * });
 */
export const UserListQuerySchema = PaginationSchema.extend({
  /** Optional search query (matches displayName or email) */
  search: z.string().optional(),
  /**
   * GHE #110 §1.1 / #99 (mention-picker prerequisite): when present, further scopes the
   * roster to users who can READ this task's resolved space (in addition to the base
   * caller-visibility scoping the endpoint always applies). Optional and backward
   * compatible — omitting it preserves the base-scoped (non-task-narrowed) roster used by
   * `AssigneePicker` and similar callers.
   */
  taskId: z.string().uuid().optional(),
  /**
   * ADR 0029 — include people who have left (`disabledAt` set) in the roster.
   *
   * Defaults to false. LOOKUP surfaces — search, the task filter bar, dashboard widget
   * filters — pass true, driven by an explicit "include disabled" control the user ticks, so
   * "what did they work on?" is always answerable. ASSIGNMENT surfaces never pass it: someone
   * who has left must not be selectable for new work, toggle or not.
   *
   * Coerced from the query string, where everything arrives as text.
   */
  includeDisabled: z
    .enum(['true', 'false'])
    .transform((v) => v === 'true')
    .optional(),
  /**
   * GHE #1291 — cuando está presente, acota el listado a quien pertenece a esa área de
   * conocimiento: sus MIEMBROS y sus LÍDERES (decisión del propietario 2026-09-20 — «líder y
   * miembro cuentan igual»; liderar y pertenecer son dos tablas distintas y se puede liderar
   * un área sin estar dentro).
   *
   * Tercera capa de acotación, con la misma forma que las dos anteriores: se aplica sobre lo
   * que la visibilidad por empresa y el `taskId` ya dejaron, nunca en su lugar.
   *
   * 🚨 El filtro va en el SERVIDOR y no en el cliente a propósito: la respuesta viene
   * paginada, así que filtrar las 10 filas recibidas daría una lista corta y un orden falso —
   * parecería que el área tiene tres personas cuando tiene treinta.
   */
  specialtyId: z.string().uuid().optional(),
  /**
   * GHE #1335 — acota a quien PUEDE RECIBIR TRABAJO en el workspace de esa lista.
   *
   * Decisión del propietario: en un workspace sólo se planifica a los *member*. El rol `VIEWER`
   * es para MIRAR otro departamento —ver si alguien está disponible— no para que le caiga
   * trabajo. De ahí el nombre del rol.
   *
   * 🚨 Un solo parámetro que dice las DOS cosas —de qué workspace y con qué regla— a propósito.
   * Con un `listId` y un `plannableOnly` por separado existirían combinaciones a medias («esta
   * lista, pero sin la regla») que nadie quiere y que habría que decidir qué significan.
   *
   * ⚠️ Es OPT-IN. Esta misma ruta la leen diecisiete pantallas —menciones, filtros, campos de
   * persona, paneles de acceso— y ninguna quiere esta regla: un observador se puede mencionar y
   * se puede buscar. Manage-gatear un listado de personas vació NUEVE superficies una vez
   * (#835); esto no vuelve a pasar por aquí.
   */
  plannableForListId: z.string().uuid().optional(),
});

/** Type inferred from UserListQuerySchema */
export type UserListQuery = z.infer<typeof UserListQuerySchema>;
